// Static site data for MB Prism Software Solutions Ltd.

export const COMPANY = {
  name: "MB Prism Software Solutions Ltd.",
  short: "MB Prism",
  tagline: "Software systems, engineered with intent.",
  director: "Manoj Bandaru",
  directorTitle: "Director",
  address: {
    line1: "220 Stiver Street",
    line2: "Russell, Ontario",
    postal: "K4R 1G9",
    country: "Canada",
  },
  phone: "+1 519-651-9462",
  hours: "Mon – Fri · 08:00 – 17:00 EST",
  email: "info@mbprism.com",
  legal: "© " + new Date().getFullYear() + " MB Prism Software Solutions Ltd. All rights reserved.",
};

export const NAV = [
  { id: "about", label: "About" },
  { id: "services", label: "Services" },
  { id: "industries", label: "Industries" },
  { id: "work", label: "Case Studies" },
  { id: "careers", label: "Careers" },
  { id: "contact", label: "Contact" },
];

export const SERVICES = [
  {
    code: "01",
    title: "Business Systems Analysis",
    desc: "Requirements engineering, process mapping and stakeholder facilitation that converts ambiguity into shipping specifications.",
    bullets: ["Requirements gathering", "Process re-engineering", "Solution architecture"],
    accent: "#2F6B1F",
  },
  {
    code: "02",
    title: "Custom Software Development",
    desc: "Production-grade web and platform engineering with React, .NET, Node.js and Python — built to ship and built to last.",
    bullets: ["Web platforms", "Internal tools", "API & integration layers"],
    accent: "#1E4FB8",
  },
  {
    code: "03",
    title: "IT Consulting",
    desc: "Strategic technology guidance for digital transformation, modernization and operational efficiency programmes.",
    bullets: ["Tech strategy", "Modernization roadmaps", "Vendor selection"],
    accent: "#FF331F",
  },
  {
    code: "04",
    title: "Cloud Solutions",
    desc: "Azure and AWS architecture, migration and managed services — secure, observable, and cost-aware by default.",
    bullets: ["Cloud migration", "Infrastructure as code", "Cost governance"],
    accent: "#F5A524",
  },
  {
    code: "05",
    title: "Data & Analytics",
    desc: "Warehouse design, BI and decision systems that turn operational data into a board-ready signal.",
    bullets: ["Data warehousing", "Power BI / Tableau", "ML enablement"],
    accent: "#7C3AED",
  },
  {
    code: "06",
    title: "Staff Augmentation",
    desc: "Vetted analysts, developers and architects embedded into your delivery teams under our quality framework.",
    bullets: ["Senior consultants", "Delivery oversight", "Flexible contracts"],
    accent: "#0A8F8F",
  },
];

export const INDUSTRIES = [
  {
    key: "finance",
    label: "Finance",
    accent: "#1E4FB8",
    headline: "Compliance-grade systems for banks, insurers and capital markets.",
    points: [
      "Core platform modernization & integration",
      "Regulatory reporting and audit trails",
      "Risk and customer onboarding workflows",
    ],
  },
  {
    key: "healthcare",
    label: "Healthcare",
    accent: "#2F6B1F",
    headline: "Clinical and operational software with privacy at the centre.",
    points: [
      "EMR & HL7/FHIR integrations",
      "Patient engagement portals",
      "PHIPA / HIPAA-aware architectures",
    ],
  },
  {
    key: "retail",
    label: "Retail",
    accent: "#F5A524",
    headline: "Commerce, inventory and customer systems that scale with you.",
    points: [
      "Omnichannel order management",
      "Inventory and POS integration",
      "Customer loyalty platforms",
    ],
  },
  {
    key: "government",
    label: "Government",
    accent: "#FF331F",
    headline: "Public-sector delivery with accountability and accessibility.",
    points: [
      "Citizen service portals",
      "Legacy modernization (mainframe to cloud)",
      "Accessibility (WCAG) compliance",
    ],
  },
  {
    key: "manufacturing",
    label: "Manufacturing",
    accent: "#7C3AED",
    headline: "Connected operations and ERP-aligned analytics.",
    points: [
      "MES and IoT data pipelines",
      "ERP integration (SAP, Dynamics)",
      "Production analytics dashboards",
    ],
  },
  {
    key: "logistics",
    label: "Logistics",
    accent: "#0A8F8F",
    headline: "Real-time visibility across fleet, warehouse and last mile.",
    points: [
      "TMS / WMS integrations",
      "Route and dispatch optimization",
      "Carrier and partner APIs",
    ],
  },
];

export const CASE_STUDIES = [
  {
    tag: "Banking",
    accent: "#1E4FB8",
    title: "Underwriting workbench replacing 14 spreadsheets",
    summary:
      "Designed and shipped a single decision surface for a North-American lender — cutting average decision time from 6 days to under 9 hours.",
    metric: "−86% turnaround",
    image:
      "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1400&q=80",
    span: "lg:col-span-8 lg:row-span-2",
  },
  {
    tag: "Healthcare",
    accent: "#2F6B1F",
    title: "Patient intake & triage portal",
    summary:
      "Multi-clinic intake platform with HL7 integration and accessibility built to WCAG 2.1 AA.",
    metric: "12 clinics live",
    image:
      "https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=1200&q=80",
    span: "lg:col-span-4",
  },
  {
    tag: "Public Sector",
    accent: "#FF331F",
    title: "Permits modernization for a municipal authority",
    summary:
      "Replaced a 1990s green-screen permits system with a cloud-native portal serving 220,000 residents.",
    metric: "220K served",
    image:
      "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1200&q=80",
    span: "lg:col-span-4",
  },
  {
    tag: "Logistics",
    accent: "#F5A524",
    title: "Fleet visibility and dispatch optimization",
    summary:
      "Real-time event ingestion across 1,400 vehicles, with route optimization shaving 11% off fuel costs.",
    metric: "−11% fuel cost",
    image:
      "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&w=1200&q=80",
    span: "lg:col-span-8",
  },
];

export const STATS = [
  { value: 120, suffix: "+", label: "Projects delivered", color: "#2F6B1F" },
  { value: 38, suffix: "", label: "Active enterprise clients", color: "#1E4FB8" },
  { value: 12, suffix: "+", label: "Years of combined practice", color: "#F5A524" },
  { value: 96, suffix: "%", label: "Client retention", color: "#FF331F" },
];

export const CAREERS = [
  {
    id: "sse",
    role: "Senior Software Engineer (Full-Stack)",
    location: "Remote · Canada",
    type: "Full-time",
    team: "Engineering",
  },
  {
    id: "data",
    role: "Data Engineer",
    location: "Remote · Canada",
    type: "Contract",
    team: "Data & Analytics",
  },
  {
    id: "swe-intern",
    role: "Software Engineering Intern",
    location: "Remote · Canada",
    type: "Internship",
    team: "Engineering",
  },
];

export const TESTIMONIALS = [
  {
    quote:
      "MB Prism replaced months of internal debate with a clear, documented system. The team’s rigour around analysis is rare.",
    author: "VP, Operations",
    org: "National Insurance Group",
  },
  {
    quote:
      "Pragmatic, senior, and accountable. They shipped a complex underwriting workflow on the date they originally promised.",
    author: "CIO",
    org: "Mid-market Lender",
  },
];

export const IMAGES = {
  hero:
    "https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=1400&q=80",
  about:
    "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1600&q=80",
  abstract:
    "https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=1600&q=80",
  team:
    "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=1400&q=80",
  logos: [
    "https://images.unsplash.com/photo-1649015931204-15a3c789e6ea?crop=entropy&cs=srgb&fm=jpg&w=400&q=80",
    "https://images.unsplash.com/photo-1750839990929-d4505ede9df2?crop=entropy&cs=srgb&fm=jpg&w=400&q=80",
    "https://images.unsplash.com/photo-1648327749277-440d4459c1f6?crop=entropy&cs=srgb&fm=jpg&w=400&q=80",
  ],
};
